// src/pages/CategoriesPage.jsx - 优化版本（限制高度，内部滚动）
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Package } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import CategoryDialog from '../components/admin/CategoryDialog';
import { categoryApi } from '../api';

const CategoriesPage = () => {
  const { language } = useApp();
  const t = translations[language];
  
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [error, setError] = useState('');

  // 加载分类列表
  const loadCategories = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('📁 正在获取分类列表...');
      
      const res = await categoryApi.getStats(); // 使用统计接口,包含商品数量
      
      console.log('✅ 分类列表响应:', res);
      
      if (res.success) {
        setStats(res.data || []);
        console.log(`✅ 成功加载 ${res.data.length} 个分类`);
      } else {
        setError(res.message || t.loadFailed || '获取分类失败');
      }
    } catch (err) {
      console.error('❌ 获取分类失败:', err);
      setError(err.message || t.loadFailed || '获取分类失败');
    } finally {
      setLoading(false);
    }
  };

  // 组件加载时获取数据
  useEffect(() => {
    loadCategories();
  }, []);

  // 打开新增对话框
  const handleAdd = () => {
    setEditingCategory(null);
    setShowDialog(true);
  };

  // 打开编辑对话框
  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowDialog(true);
  };

  // 删除分类
  const handleDelete = async (categoryId, categoryName, productCount) => {
    if (productCount > 0) {
      alert(
        language === 'en' 
          ? `Cannot delete "${categoryName}" because it has ${productCount} products.`
          : language === 'km'
          ? `មិនអាចលុប "${categoryName}" បានទេ ព្រោះវាមានទំនិញ ${productCount} ។`
          : `无法删除"${categoryName}"，因为该分类下还有 ${productCount} 个商品。`
      );
      return;
    }

    const confirmText = language === 'en' 
      ? `Are you sure you want to delete "${categoryName}"?`
      : language === 'km'
      ? `តើអ្នកប្រាកដថាចង់លុប "${categoryName}" មែនទេ?`
      : `确定要删除分类"${categoryName}"吗？`;
    
    if (!window.confirm(confirmText)) {
      return;
    }

    try {
      console.log('🗑️ 正在删除分类:', categoryId);
      
      const res = await categoryApi.delete(categoryId);
      
      if (res.success) {
        console.log('✅ 删除成功');
        loadCategories();
        alert(t.deleteSuccess || '删除成功！');
      } else {
        alert(res.message || t.deleteFailed || '删除失败');
      }
    } catch (err) {
      console.error('❌ 删除分类失败:', err);
      alert(err.message || t.deleteFailed || '删除失败，请重试');
    }
  };

  // 提交分类 (新增或编辑)
  const handleSubmit = async (formData) => {
    try {
      console.log('💾 提交分类数据:', formData);
      
      let res;
      if (editingCategory) {
        // 编辑
        res = await categoryApi.update(editingCategory.id, formData);
      } else {
        // 新增
        res = await categoryApi.create(formData);
      }
      
      if (res.success) {
        console.log('✅ 保存成功');
        loadCategories();
        setShowDialog(false);
        setEditingCategory(null);
        alert(
          editingCategory 
            ? (t.updateSuccess || '更新成功！')
            : (t.addSuccess || '添加成功！')
        );
      } else {
        alert(res.message || t.operationFailed || '操作失败');
      }
    } catch (err) {
      console.error('❌ 保存分类失败:', err);
      alert(err.message || t.operationFailed || '操作失败，请重试');
    }
  };

  // 获取分类显示名称
  const getCategoryName = (category) => {
    switch (language) {
      case 'en':
        return category.name_en || category.name;
      case 'km':
        return category.name_km || category.name;
      default:
        return category.name;
    }
  };

  return (
    <div className="h-full flex flex-col gap-6">
      {/* 头部 - 固定 */}
      <div className="flex-shrink-0 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            {language === 'en' ? 'Category Management' : language === 'km' ? 'គ្រប់គ្រងប្រភេទ' : '分类管理'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {language === 'en' ? 'Total' : language === 'km' ? 'សរុប' : '共'} {stats.length} {language === 'en' ? 'categories' : language === 'km' ? 'ប្រភេទ' : '个分类'}
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center"
        >
          <Plus className="w-5 h-5 mr-2" />
          {language === 'en' ? 'Add Category' : language === 'km' ? 'បន្ថែមប្រភេទ' : '添加分类'}
        </button>
      </div>

      {/* 错误提示 - 固定 */}
      {error && (
        <div className="flex-shrink-0 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
          <button 
            onClick={loadCategories}
            className="ml-4 underline"
          >
            {t.retry || '重试'}
          </button>
        </div>
      )}

      {/* 表格区域 - 可滚动 */}
      <div className="flex-1 min-h-0 bg-white rounded-xl shadow-lg flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">
                {language === 'en' ? 'Loading...' : language === 'km' ? 'កំពុងផ្ទុក...' : '加载中...'}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {language === 'en' ? 'Category Name' : language === 'km' ? 'ឈ្មោះប្រភេទ' : '分类名称'}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    <Package className="w-4 h-4 inline mr-1" />
                    {language === 'en' ? 'Products' : language === 'km' ? 'ទំនិញ' : '商品数量'}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {language === 'en' ? 'Total Stock' : language === 'km' ? 'ស្តុកសរុប' : '总库存'}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {language === 'en' ? 'Total Value' : language === 'km' ? 'តម្លៃសរុប' : '总价值'}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    {language === 'en' ? 'Actions' : language === 'km' ? 'សកម្មភាព' : '操作'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center text-gray-400">
                      {language === 'en' ? 'No categories found' : language === 'km' ? 'រកមិនឃើញប្រភេទ' : '暂无分类'}
                    </td>
                  </tr>
                ) : (
                  stats.map((category) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {category.id}
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {getCategoryName(category)}
                          </p>
                          {language !== 'zh' && category.name && (
                            <p className="text-xs text-gray-500">{category.name}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {category.product_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {category.total_stock || 0}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-blue-600 text-right">
                        ¥{parseFloat(category.total_value || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center space-x-2">
                          {/* 编辑按钮 */}
                          <button
                            onClick={() => handleEdit(category)}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title={t.edit}
                          >
                            <Edit className="w-4 h-4" />
                          </button>

                          {/* 删除按钮 */}
                          <button
                            onClick={() => handleDelete(category.id, getCategoryName(category), category.product_count)}
                            className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                            title={t.delete}
                            disabled={category.product_count > 0}
                          >
                            <Trash2 className={`w-4 h-4 ${category.product_count > 0 ? 'opacity-30' : ''}`} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 分类对话框 */}
      {showDialog && (
        <CategoryDialog
          category={editingCategory}
          onClose={() => {
            setShowDialog(false);
            setEditingCategory(null);
          }}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};

export default CategoriesPage;